import { Component, DoCheck, Input, IterableDiffers, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ElementAttribute } from '@models/editor';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { mixedString } from 'icreate/common/models/mimic';
import _ from 'lodash';

@Component({
    selector: 'app-rotation-options',
    templateUrl: './rotation-options.component.html',
    styleUrls: ['./../design-side-menu/design-side-menu.component.css'],
    standalone: false,
})
export class RotationOptionsComponent implements DoCheck, OnChanges, OnDestroy {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly ROTATION_MAX: number = 359;
    readonly MIXED_STRING = mixedString;

    @Input() selectedElements;

    rotation: string;

    iterableDiffer;
    observer: MutationObserver;

    constructor(
        private iterableDiffers: IterableDiffers,
        private selectionService: SelectedElementsService,
        private keyFilterService: KeyFilterSerivce
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            if (this.selectedElements.length > 1) {
                this.rotation = mixedString;
                return;
            } else {
                const transform = this.selectedElements[0].getAttribute(ElementAttribute.transform);
                if (transform) {
                    const match = transform.match(/rotate\((-?\d+(\.\d+)?)/);
                    if (match) {
                        this.rotation = Number(match[1]).toFixed(0);
                        return;
                    }
                }
                this.rotation = '0';
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedElements) {
            this.observer?.disconnect();
            this.observer = new MutationObserver(
                _.debounce((event: any) => {
                    const transform = event[0].target.getAttribute(ElementAttribute.transform);
                    if (transform) {
                        const match = transform.match(/rotate\((-?\d+(\.\d+)?)/);
                        if (match) {
                            this.rotation = Number(match[1]).toFixed(0);
                        }
                        return;
                    }
                    this.rotation = '0';
                })
            );

            const transformElement = document.querySelectorAll('#selectorGroup0')[0];
            this.observer.observe(transformElement, {
                attributes: true,
                attributeFilter: [ElementAttribute.transform],
            });
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    handleRotationChange({ name, value }) {
        if (!value) value = 0;
        this.selectedElements.forEach(element => {
            const bbox = element.getBBox();
            if (!bbox) return;
            const centerX = bbox.x + bbox.width / 2;
            const centerY = bbox.y + bbox.height / 2;
            if (!centerX || !centerY) return;

            const transform = element.getAttribute(ElementAttribute.transform) ?? '';
            const baseTransform = transform.replace(/^\s*rotate\([^)]*\)\s*/, '');
            let center = { x: centerX, y: centerY };
            if (baseTransform) {
                element.setAttribute(ElementAttribute.transform, baseTransform);
                const matrix = element.transform.baseVal.consolidate()?.matrix;
                if (matrix) {
                    center = new DOMPoint(centerX, centerY).matrixTransform(matrix);
                }
            }
            element.setAttribute(
                ElementAttribute.transform,
                `rotate(${value}, ${center.x}, ${center.y}) ${baseTransform}`.trim()
            );
        });

        this.selectionService.setSelectedElements(this.selectedElements);
    }

    validRotationKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event, value } = eventObj;
        const result = this.keyFilterService.shouldPreventDefault(event, this.ALLOWED_KEYS);
        if (result) event.preventDefault();
    }
}
